export type ResultActionId =
  | 'save_local'
  | 'print_redacted_summary'
  | 'print_with_evidence'
  | 'print_with_full_birth'
  | 'export_private_backup'
  | 'export_redacted_share'
  | 'delete_saved_chart'
  | 'clear_all_local_charts';

export interface ResultActionViewModel {
  id: ResultActionId;
  label: string;
  category: 'save' | 'print' | 'export' | 'delete';
  networkRequired: false;
  containsSensitiveBirthData: boolean;
  explicitOptInRequired: boolean;
  destructive: boolean;
  confirmationRequired: boolean;
  recommendedDefault: boolean;
  disclosure: string;
}

export interface ResultActionsViewModel {
  model: 'RESULT-ACTIONS-VIEW-0.1.0';
  actions: readonly ResultActionViewModel[];
  disclosure: string;
}

const ACTIONS: readonly ResultActionViewModel[] = Object.freeze([
  Object.freeze({
    id: 'save_local' as const,
    label: '儲存到此裝置',
    category: 'save' as const,
    networkRequired: false as const,
    containsSensitiveBirthData: true,
    explicitOptInRequired: true,
    destructive: false,
    confirmationRequired: false,
    recommendedDefault: false,
    disclosure: '只有使用者主動點選才寫入本機專用儲存空間；不會自動保存或上傳。',
  }),
  Object.freeze({
    id: 'print_redacted_summary' as const,
    label: '列印隱私版摘要',
    category: 'print' as const,
    networkRequired: false as const,
    containsSensitiveBirthData: false,
    explicitOptInRequired: false,
    destructive: false,
    confirmationRequired: false,
    recommendedDefault: true,
    disclosure: '預設隱去出生日期、時辰與性別，且不附詳細 Evidence Appendix。',
  }),
  Object.freeze({
    id: 'print_with_evidence' as const,
    label: '列印含判讀依據',
    category: 'print' as const,
    networkRequired: false as const,
    containsSensitiveBirthData: false,
    explicitOptInRequired: true,
    destructive: false,
    confirmationRequired: false,
    recommendedDefault: false,
    disclosure: '明確選擇後才加入 Rule、Source 與 Evidence Appendix；出生資料仍維持隱去。',
  }),
  Object.freeze({
    id: 'print_with_full_birth' as const,
    label: '列印完整出生資料',
    category: 'print' as const,
    networkRequired: false as const,
    containsSensitiveBirthData: true,
    explicitOptInRequired: true,
    destructive: false,
    confirmationRequired: true,
    recommendedDefault: false,
    disclosure: '會把出生日期、時辰與性別放入列印內容，需再次確認。',
  }),
  Object.freeze({
    id: 'export_private_backup' as const,
    label: '匯出私密備份',
    category: 'export' as const,
    networkRequired: false as const,
    containsSensitiveBirthData: true,
    explicitOptInRequired: true,
    destructive: false,
    confirmationRequired: true,
    recommendedDefault: false,
    disclosure: '可重新匯入，包含完整出生與命盤資料；只應存放於信任位置。',
  }),
  Object.freeze({
    id: 'export_redacted_share' as const,
    label: '匯出隱私分享版',
    category: 'export' as const,
    networkRequired: false as const,
    containsSensitiveBirthData: false,
    explicitOptInRequired: true,
    destructive: false,
    confirmationRequired: false,
    recommendedDefault: true,
    disclosure: '移除出生資料與 fingerprint，只保留命盤摘要與版本來源；此檔案不可還原本機資料。',
  }),
  Object.freeze({
    id: 'delete_saved_chart' as const,
    label: '刪除此命盤',
    category: 'delete' as const,
    networkRequired: false as const,
    containsSensitiveBirthData: false,
    explicitOptInRequired: true,
    destructive: true,
    confirmationRequired: true,
    recommendedDefault: false,
    disclosure: '只刪除此網站本機 namespace 中指定命盤，不影響其他網站資料。',
  }),
  Object.freeze({
    id: 'clear_all_local_charts' as const,
    label: '刪除全部本機命盤',
    category: 'delete' as const,
    networkRequired: false as const,
    containsSensitiveBirthData: false,
    explicitOptInRequired: true,
    destructive: true,
    confirmationRequired: true,
    recommendedDefault: false,
    disclosure: '清除前必須確認；只清理本專案專用 namespace，不碰其他 LocalStorage key。',
  }),
]);

export function buildResultActionsViewModel(): ResultActionsViewModel {
  return Object.freeze({
    model: 'RESULT-ACTIONS-VIEW-0.1.0',
    actions: ACTIONS,
    disclosure: '結果頁所有保存、列印、匯出與刪除功能都在瀏覽器本機完成；安全預設不包含完整出生資料，也不需要網路。',
  });
}
